/**
 * Turns the framing calculator's raw, implementation-shaped spec object into the clean,
 * human-readable record actually stored on an order.
 *
 * This is a pure, additive layer bolted on AFTER the fact — nothing here changes what the live
 * preview or the calculator itself consume. Those still read the raw `FramingSpecs`-shaped object
 * the calculator emits (via `orderForm.framingSpecs` on the artwork/set pages, and the `specs` ref
 * inside CartItemFraming.vue) exactly as before. This module only reshapes a COPY of that data at
 * the moment an order payload is built, right before it is handed to `processPayment`.
 *
 * The three checkouts (artwork, artwork-set, cart) all compose from the same two building blocks:
 *   - `PieceFraming` — one physical artwork's own choice and build.
 *   - `SetFramingRecord` — one shared choice/build priced across a set's available pieces.
 * A cart item is either of those, wrapped with just enough (`cartItemId`, `itemType`) to say which
 * cart line it belongs to. See `CartFramingRecord` below.
 */
import type { FramingArtworkType, FramingComponents, FramingMode, FramingSpecs } from './framingCalculator';

export type FramingChoice = 'unframed' | 'framed' | 'stretched';

/**
 * Why a piece that is part of the order isn't being charged for framing.
 *
 * Shared across a single piece's own build (which can only ever be `out_of_range` or
 * `size_unavailable` — a lone artwork has no OTHER piece to be excluded relative to) and a set's
 * per-piece list (which can also be `already_framed` or `not_framable`, since there the one shared
 * spec may simply not reach a given piece). One enum, used everywhere, rather than a different
 * shape of "why zero" in each of the three checkouts.
 */
export type FramingUnpricedReason = 'already_framed' | 'not_framable' | 'out_of_range' | 'size_unavailable';

export interface FramingMountRecord {
  type: string;
  widthIn: number;
  /** Whether the buyer actually turned this add-on on. When false, `cost` is 0 — the record is
   *  still present so a reader can see mount was AVAILABLE and simply not chosen, not that it
   *  never applied to this build at all (that case omits the key entirely — see buildComponents). */
  applied: boolean;
  cost: number | null;
}
export interface FramingGlassRecord {
  type: string;
  cost: number | null;
}
export interface FramingBoardRecord {
  type: string;
  cost: number | null;
}
export interface FramingSideDepthRecord {
  ratePerFt: number;
  /** Whether the buyer actually turned this add-on on. When false, `cost` is 0 — the record is
   *  still present so a reader can see side depth was AVAILABLE and simply not chosen, not that
   *  it never applied to this build at all (that case omits the key — see buildComponents). */
  applied: boolean;
  cost: number | null;
}
export interface FramingSheetRecord {
  ratePerSqFt: number;
  cost: number | null;
}
export interface FramingStretcherRecord {
  bar: string;
  cost: number | null;
}
export interface FramingFrameRecord {
  type: string;
  material: string;
  finish: string | null;
  /** The paint/timber colour swatch the buyer picked — only meaningful (non-null) on a Wooden
   *  frame finished Painted Polished or Double shade; every other combination has no colour choice
   *  to make at all, so this stays null rather than reporting a swatch nobody chose. */
  color: string | null;
  frontIn: number;
  depthIn: number;
  floaterSpaceIn: number | null;
  cost: number | null;
}

/**
 * Which of these keys can be present at all is decided entirely by artwork category + choice:
 *   - Paper + framed:  mount, glass, board, sideDepth, sheet, stretcher, frame
 *   - Canvas + framed: stretcher, frame
 *   - Stretched:       stretcher only
 *   - Unframed:        none of these — the whole `build` is absent, see PieceFraming.
 *
 * `mount` and `sideDepth` are optional ADD-ONS rather than mandatory parts of the build: whenever
 * the category/mode combination could include one (Paper + framed), its key is present either way
 * — with `applied: false` and `cost: 0` if the buyer left it off, `applied: true` and a real cost
 * if they turned it on. The key is only OMITTED when it could never apply at all (Canvas, or any
 * Stretched build) — that is the one case with no add-on to report on. Every other key here
 * (glass/board/sheet/stretcher/frame) has no on/off toggle in the first place, so it simply appears
 * whenever it's part of the build and is absent otherwise.
 */
export interface FramingBuildComponents {
  mount?: FramingMountRecord;
  glass?: FramingGlassRecord;
  board?: FramingBoardRecord;
  sideDepth?: FramingSideDepthRecord;
  sheet?: FramingSheetRecord;
  stretcher?: FramingStretcherRecord;
  frame?: FramingFrameRecord;
}

export interface FramingBuild {
  components: FramingBuildComponents;
  /** 0 whenever `reason` is set — an unpriced build is never partially charged. */
  totalCost: number;
  reason: FramingUnpricedReason | null;
}

export interface PieceFraming {
  artworkId: string | number;
  artworkName: string;
  sizeLabel: string;
  widthIn: number;
  heightIn: number;
  artworkCategory: FramingArtworkType;
  /** Whether the piece already had a frame at the time of this order — informational only, it no
   *  longer changes which choices are offered. */
  isAlreadyFramed: boolean;
  choice: FramingChoice;
  /** Absent entirely when `choice` is `'unframed'` — there is nothing to build. */
  build?: FramingBuild;
}

export interface SetPieceSummary {
  artworkId: string | number;
  artworkName: string;
  sizeLabel: string;
  widthIn: number;
  heightIn: number;
  artworkCategory: FramingArtworkType;
  /** True on the one piece the set's shared spec was actually dialed in on (see `configuredOn`). */
  isConfiguredOn: boolean;
  cost: number;
  reason: FramingUnpricedReason | null;
}

export interface SetFramingRecord {
  scope: 'set';
  setId: string | number;
  choice: FramingChoice;
  /** The piece the one shared specification was configured on — null only if `choice` is
   *  `'unframed'`, or nothing in the set could ever be framed at all. */
  configuredOn: { artworkId: string | number; artworkName: string; sizeLabel: string } | null;
  /** The ONE spec selection, shared by every piece below — null when `choice` is `'unframed'`. */
  build: FramingBuild | null;
  /**
   * Only pieces actually being purchased. A Sold/Reserved/Gift piece in this set is never part of
   * the order, so it never appears here at all — not as a ₹0 entry, not with a reason. A piece
   * that IS being purchased but can't take the spec (already framed, not framable, out of range)
   * still appears, at ₹0, with a `reason` — the buyer is paying for it, they should see why it
   * isn't charged.
   */
  pieces: SetPieceSummary[];
  totalCost: number;
}

export interface CartArtworkFramingItem {
  cartItemId: string;
  itemType: 'artwork';
  artworkId: string | number;
  artworkName: string;
  /** Null when this item has nothing to configure (non-framable) — not an empty object. */
  framing: PieceFraming | null;
}

export interface CartSetFramingItem {
  cartItemId: string;
  itemType: 'set';
  setId: string | number;
  setName: string;
  /** Null when no piece in this set can take a new frame. */
  framing: SetFramingRecord | null;
}

export type CartFramingItem = CartArtworkFramingItem | CartSetFramingItem;

export interface CartFramingRecord {
  scope: 'cart';
  totalCost: number;
  items: CartFramingItem[];
}

/** What the standalone artwork checkout stores — a `PieceFraming` tagged with its scope, so all
 *  three checkouts' stored records carry the same kind of discriminator at the top. */
export type ArtworkFramingRecord = { scope: 'artwork' } & PieceFraming;

const CHOICE_MAP: Record<string, FramingChoice> = {
  Unframed: 'unframed',
  Framed: 'framed',
  Stretched: 'stretched',
};

export function toFramingChoice(raw: string | null | undefined): FramingChoice | null {
  return raw ? (CHOICE_MAP[raw] ?? null) : null;
}

/**
 * The four "why isn't this piece charged" sentences the set/cart breakdown tables already show on
 * screen, mapped to the fixed reason codes above. Kept as a separate mapping (rather than changing
 * what `framingByArtwork` produces) so the on-screen wording and its component are untouched by
 * this — if that wording is ever edited, this map has to be updated alongside it.
 */
export function noteToFramingReason(note: string | null | undefined): FramingUnpricedReason | null {
  switch (note) {
    case 'Already framed':
      return 'already_framed';
    case 'Not framable':
      return 'not_framable';
    case 'Size unavailable':
      return 'size_unavailable';
    case 'Size beyond online pricing — we will confirm this piece':
      return 'out_of_range';
    default:
      return null;
  }
}

/** Exactly what `FramingCalculator`'s `update:specifications` emits: the sanitized spec fields
 *  plus the pricing extras layered on top (see FramingCalculator.vue's emit call). */
type RawFramingSpecs = Partial<FramingSpecs> & {
  artworkType?: FramingArtworkType;
  mode?: FramingMode;
  calculatedTotal?: number;
  outOfRange?: boolean;
  components?: FramingComponents;
  // Deliberately absent from FramingSpecs proper (it doesn't affect price — see that type's own
  // comment), but it's a real physical choice the buyer makes and needs to reach the order. Only
  // ever non-null on a Wooden frame finished Painted Polished or Double shade.
  frameColor?: string | null;
};

/** The calculator only ever stores frameColor as a short swatch key ('polished'/'white'/'black');
 *  everywhere else in this record uses the human label, so this does too. */
const FRAME_COLOR_LABELS: Record<string, string> = {
  polished: 'Wooden Polished',
  white: 'Wooden Painted White',
  black: 'Wooden Painted Black',
};

/** The reverse of the map above — needed to redraw `FrameVisualizer`'s preview from a stored
 *  record, which only ever carries the human label (see `framingBuildToVisualizerSpecs`). */
const FRAME_COLOR_KEYS: Record<string, string> = Object.fromEntries(
  Object.entries(FRAME_COLOR_LABELS).map(([key, label]) => [label, key])
);

function componentCost(result?: { cost?: number; outOfRange?: boolean }): number | null {
  if (!result) return null;
  return result.outOfRange ? null : (result.cost ?? 0);
}

function buildComponents(rawSpecs: RawFramingSpecs): FramingBuildComponents {
  const c = rawSpecs.components;
  const out: FramingBuildComponents = {};

  // mount/sideDepth are optional ADD-ONS, not mandatory parts of the build — the calculator's own
  // `applicable` flag on these two conflates two different things (Canvas can never have either,
  // vs. Paper simply left the toggle off), so it can't tell us on its own whether to omit the key
  // or include it with applied:false. This re-derives "could this ever apply here" directly from
  // category + mode, independent of whether the buyer actually turned it on.
  const couldHaveOptionalAddOns = rawSpecs.artworkType === 'Paper' && rawSpecs.mode !== 'stretch';

  if (couldHaveOptionalAddOns) {
    const applied = !!rawSpecs.wantMount;
    out.mount = {
      type: rawSpecs.mountType ?? '',
      widthIn: Number(rawSpecs.mountWidth) || 0,
      applied,
      cost: applied ? componentCost(c?.mount) : 0,
    };
  }
  if (c?.glass?.applicable) {
    out.glass = { type: rawSpecs.glassType ?? '', cost: componentCost(c.glass) };
  }
  if (c?.board?.applicable) {
    out.board = { type: rawSpecs.boardType ?? '', cost: componentCost(c.board) };
  }
  if (couldHaveOptionalAddOns) {
    const applied = !!rawSpecs.wantSideDepth;
    out.sideDepth = {
      ratePerFt: Number(rawSpecs.sideDepthRate) || 0,
      applied,
      cost: applied ? componentCost(c?.sideDepth) : 0,
    };
  }
  if (c?.sheet?.applicable) {
    out.sheet = { ratePerSqFt: Number(rawSpecs.sheetRate) || 0, cost: componentCost(c.sheet) };
  }
  if (c?.stretcher?.applicable) {
    out.stretcher = { bar: rawSpecs.stretcherBar ?? '', cost: componentCost(c.stretcher) };
  }
  if (c?.frame?.applicable) {
    const finish = rawSpecs.frameMaterial === 'Wooden' ? (rawSpecs.frameFinish ?? null) : null;
    const hasColorChoice = finish === 'Painted Polished' || finish === 'Double shade';
    out.frame = {
      type: rawSpecs.frameType ?? '',
      material: rawSpecs.frameMaterial ?? '',
      finish,
      color: hasColorChoice && rawSpecs.frameColor
        ? (FRAME_COLOR_LABELS[rawSpecs.frameColor] ?? rawSpecs.frameColor)
        : null,
      frontIn: Number(rawSpecs.frameFront) || 0,
      depthIn: Number(rawSpecs.frameDepth) || 0,
      floaterSpaceIn: rawSpecs.frameType === 'Floater Frame' ? (Number(rawSpecs.floaterSpace) || 0) : null,
      cost: componentCost(c.frame),
    };
  }
  return out;
}

/**
 * Builds the `build` block for ONE priced spec — used directly for a standalone artwork, and for a
 * set's shared spec (priced against its configured-on piece's own size).
 *
 * `widthIn`/`heightIn` here are NOT read off `rawSpecs` — they are the caller's own parsed
 * dimensions for whichever piece this build is being priced against, since the calculator's raw
 * emission carries no size of its own.
 */
export function buildFramingBuild(
  rawSpecs: RawFramingSpecs | null | undefined,
  widthIn: number,
  heightIn: number,
): FramingBuild | null {
  if (!rawSpecs) return null;
  if (!(widthIn > 0) || !(heightIn > 0)) {
    return { components: {}, totalCost: 0, reason: 'size_unavailable' };
  }
  const outOfRange = !!rawSpecs.outOfRange;
  return {
    components: buildComponents(rawSpecs),
    totalCost: outOfRange ? 0 : (Number(rawSpecs.calculatedTotal) || 0),
    reason: outOfRange ? 'out_of_range' : null,
  };
}

/** The full record for one standalone artwork, or one artwork sitting inside the cart. */
export function buildPieceFraming(input: {
  artworkId: string | number;
  artworkName: string;
  sizeLabel: string;
  widthIn: number;
  heightIn: number;
  artworkCategory: FramingArtworkType;
  isAlreadyFramed: boolean;
  /** The buyer-facing label: 'Unframed' | 'Framed' | 'Stretched'. */
  choice: string | null | undefined;
  rawSpecs: RawFramingSpecs | null | undefined;
}): PieceFraming | null {
  const choice = toFramingChoice(input.choice);
  if (!choice) return null;

  const base: PieceFraming = {
    artworkId: input.artworkId,
    artworkName: input.artworkName,
    sizeLabel: input.sizeLabel,
    widthIn: input.widthIn,
    heightIn: input.heightIn,
    artworkCategory: input.artworkCategory,
    isAlreadyFramed: input.isAlreadyFramed,
    choice,
  };
  if (choice === 'unframed') return base;

  const build = buildFramingBuild(input.rawSpecs, input.widthIn, input.heightIn);
  return build ? { ...base, build } : base;
}

/** The full record for a set's ONE shared choice, priced across its available pieces. Used both
 *  for the standalone set checkout and for a set sitting inside the cart. */
export function buildSetFramingRecord(input: {
  setId: string | number;
  /** The buyer-facing label: 'Unframed' | 'Framed' | 'Stretched'. */
  choice: string | null | undefined;
  configuredOn: { artworkId: string | number; artworkName: string; sizeLabel: string } | null;
  rawSpecs: RawFramingSpecs | null | undefined;
  configuredOnWidthIn: number;
  configuredOnHeightIn: number;
  /** Only pieces actually available for purchase — the caller is responsible for having already
   *  dropped Sold/Reserved/Gift pieces before this point; this function never re-checks that. */
  pieces: Array<{
    artworkId: string | number;
    artworkName: string;
    sizeLabel: string;
    widthIn: number;
    heightIn: number;
    artworkCategory: FramingArtworkType;
    cost: number;
    reason: FramingUnpricedReason | null;
  }>;
}): SetFramingRecord | null {
  const choice = toFramingChoice(input.choice);
  if (!choice) return null;

  const build =
    choice === 'unframed' ? null : buildFramingBuild(input.rawSpecs, input.configuredOnWidthIn, input.configuredOnHeightIn);

  const pieces: SetPieceSummary[] = input.pieces.map((p) => ({
    artworkId: p.artworkId,
    artworkName: p.artworkName,
    sizeLabel: p.sizeLabel,
    widthIn: p.widthIn,
    heightIn: p.heightIn,
    artworkCategory: p.artworkCategory,
    isConfiguredOn: input.configuredOn?.artworkId === p.artworkId,
    cost: p.cost,
    reason: p.reason,
  }));

  return {
    scope: 'set',
    setId: input.setId,
    choice,
    configuredOn: input.configuredOn,
    build,
    pieces,
    totalCost: pieces.reduce((sum, p) => sum + (p.cost || 0), 0),
  };
}

/**
 * Paper + Framed offers Mount and Side Depth as two independent optional add-ons (see
 * FramingBuildComponents above), and either could reasonably be left off on its own — but leaving
 * BOTH off means nothing is holding the glass off the artwork's surface at all, so checkout
 * requires at least one before it can be paid for. Never applies to Canvas (neither option exists
 * for it) or to a Stretched build (no glass in the first place).
 *
 * Takes the calculator's raw emitted spec directly — the same object already sitting in
 * `orderForm.framingSpecs` / the `specs` ref, so every checkout can call this with what it already
 * has in hand at validation time, no reshaping required.
 */
/**
 * Rebuilds the shape `FrameVisualizer`'s `specifications` prop expects from a STORED, already-
 * cleaned `FramingBuild` — the reverse of `buildComponents` above. Used only to redraw a past
 * order's preview (e.g. the internal Sales Summary "eye" button), where the calculator's raw,
 * live-editing spec object was never stored on the order — only this cleaned record was.
 *
 * Only fields `FrameVisualizer` actually reads for drawing are filled in — `boardType`, `sheetRate`
 * etc. affect price only, never the picture, so the cleaned record (which already dropped their
 * live-editing shape) has nothing useful to give back for them.
 */
export function framingBuildToVisualizerSpecs(
  build: FramingBuild,
  artworkCategory: FramingArtworkType
): Record<string, any> {
  const c = build.components;
  return {
    artworkType: artworkCategory,
    frameType: c.frame?.type,
    frameMaterial: c.frame?.material,
    frameFinish: c.frame?.finish,
    frameColor: c.frame?.color ? (FRAME_COLOR_KEYS[c.frame.color] ?? null) : null,
    frameFront: c.frame?.frontIn,
    frameDepth: c.frame?.depthIn,
    floaterSpace: c.frame?.floaterSpaceIn ?? undefined,
    wantMount: c.mount?.applied ?? false,
    mountWidth: c.mount?.widthIn,
    wantSideDepth: c.sideDepth?.applied ?? false,
    glassType: c.glass?.type,
    stretcherBar: c.stretcher?.bar,
  };
}

export function needsMountOrSideDepth(
  rawSpecs: { artworkType?: FramingArtworkType; mode?: FramingMode; wantMount?: boolean; wantSideDepth?: boolean } | null | undefined
): boolean {
  if (!rawSpecs) return false;
  if (rawSpecs.artworkType !== 'Paper' || rawSpecs.mode === 'stretch') return false;
  return !rawSpecs.wantMount && !rawSpecs.wantSideDepth;
}
